import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Row, Space, Typography } from 'antd';
import { homeCopyEn } from '../../content/homeCopy.en';
import styles from './Home.module.css';

const { Title, Paragraph, Text } = Typography;

const useInView = <T extends HTMLElement>(threshold = 0.25): [React.RefObject<T | null>, boolean] => {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, threshold]);

  return [ref, inView];
};

const JourneyBeatRow: React.FC<{
  title: string;
  description: string;
  imageSrc: string;
  alt: string;
  reverse: boolean;
}> = ({ title, description, imageSrc, alt, reverse }) => {
  const [ref, inView] = useInView<HTMLDivElement>(0.28);

  return (
    <div
      ref={ref}
      className={[
        styles.journeyBeat,
        reverse ? styles.journeyBeatReverse : '',
        inView ? styles.inView : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.journeyCopy}>
        <Title level={3} className={styles.journeyBeatTitle}>
          {title}
        </Title>
        <Paragraph className={styles.journeyBeatText}>{description}</Paragraph>
      </div>
      <figure className={styles.journeyPhone}>
        <div className={styles.screenshotFrame}>
          <img src={imageSrc} alt={alt} className={styles.screenshotImage} loading="lazy" />
        </div>
      </figure>
    </div>
  );
};

const PlatformsSection: React.FC = () => {
  const [ref, inView] = useInView<HTMLElement>(0.22);
  const { platforms } = homeCopyEn;

  return (
    <section
      ref={ref}
      id="platforms"
      className={[styles.platforms, inView ? styles.inView : ''].filter(Boolean).join(' ')}
    >
      <div className={styles.container}>
        <div className={styles.platformsLayout}>
          <div className={styles.platformsCopy}>
            <Text className={styles.eyebrow}>{platforms.eyebrow}</Text>
            <Title level={2} className={styles.platformsTitle}>
              {platforms.title}
            </Title>
            <Paragraph className={styles.platformsText}>{platforms.description}</Paragraph>
            <div className={styles.platformsActions}>
              <Link to="/signin">
                <Button
                  type="primary"
                  size="large"
                  className={styles.primaryButton}
                  style={{ background: '#478ac9', borderColor: '#478ac9' }}
                >
                  {platforms.primaryCta}
                </Button>
              </Link>
              <Link to="/app">
                <Button size="large" className={styles.secondaryButton}>
                  {platforms.secondaryCta}
                </Button>
              </Link>
            </div>
          </div>

          <div className={styles.platformsStage}>
            <figure className={styles.browserFrame}>
              <div className={styles.browserChrome}>
                <span className={styles.browserDots} aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <div className={styles.browserAddress}>portcierge.com</div>
              </div>
              <img
                src={platforms.webImageSrc}
                alt={platforms.webImageAlt}
                className={styles.browserImage}
                loading="lazy"
              />
            </figure>
            <figure className={styles.platformsPhone}>
              <div className={styles.screenshotFrame}>
                <img
                  src={platforms.phoneImageSrc}
                  alt={platforms.phoneImageAlt}
                  className={styles.screenshotImage}
                  loading="lazy"
                />
              </div>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home: React.FC = () => {
  const resolvePublicAssetUrl = (path: string): string => {
    // CRA builds with `homepage: "."` can yield `PUBLIC_URL="."` which makes relative URLs.
    // When those relative URLs are used inside CSS files (e.g., via CSS variables),
    // they can resolve relative to `/static/css/...` in production and break.
    //
    // Using an absolute URL avoids CSS-relative resolution issues.
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return new URL(normalized, window.location.href).toString();
  };

  const scrollToFeatures = (): void => {
    const el = document.getElementById('features');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  let beatIndex = 0;

  return (
    <div className={styles.home}>
      <section className={styles.hero}>
        <div className={styles.heroMedia} aria-hidden="true">
          <img
            src={homeCopyEn.hero.imageSrc}
            alt=""
            fetchPriority="high"
            decoding="async"
            className={styles.heroMediaImage}
          />
        </div>
        <div className={styles.heroScrim} aria-hidden="true" />
        <div className={styles.heroInner}>
          <img
            src={homeCopyEn.hero.iconSrc}
            alt={homeCopyEn.hero.iconAlt}
            className={styles.heroIcon}
          />
          <p className={styles.heroBrand}>{homeCopyEn.hero.eyebrow}</p>
          <Title className={styles.heroTitle}>{homeCopyEn.hero.title}</Title>
          <Paragraph className={styles.heroSubtitle}>{homeCopyEn.hero.subtitle}</Paragraph>
          <div className={styles.heroActions}>
            <Button type="primary" size="large" onClick={scrollToFeatures} className={styles.primaryButton}>
              {homeCopyEn.hero.primaryCta}
            </Button>
            <Button size="large" onClick={scrollToFeatures} className={styles.secondaryButton}>
              {homeCopyEn.hero.secondaryCta}
            </Button>
          </div>
        </div>
      </section>

      <PlatformsSection />

      <section
        className={styles.imageBreak}
        style={{
          ['--bgImage' as unknown as string]: `url(${resolvePublicAssetUrl(homeCopyEn.portByPortBreak.imageSrc)})`,
        }}
      >
        <div className={styles.imageBreakInner}>
          <div className={styles.imageBreakPanel}>
            <Text className={styles.eyebrow}>{homeCopyEn.portByPortBreak.eyebrow}</Text>
            <Title level={2} className={styles.imageBreakTitle}>
              {homeCopyEn.portByPortBreak.title}
            </Title>
            <Paragraph className={styles.imageBreakText}>{homeCopyEn.portByPortBreak.description}</Paragraph>
          </div>
        </div>
      </section>

      <section className={styles.section} id="features">
        <div className={styles.container}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Title level={2} className={styles.sectionTitle}>
              {homeCopyEn.journey.title}
            </Title>
            <Paragraph className={styles.sectionSubtitle}>{homeCopyEn.journey.subtitle}</Paragraph>
          </Space>

          <div className={styles.journey}>
            {homeCopyEn.journey.chapters.map((chapter) => (
              <div key={chapter.id} className={styles.journeyChapter} id={`journey-${chapter.id}`}>
                <div className={styles.journeyChapterHeader}>
                  <Title level={3} className={styles.journeyChapterTitle}>
                    {chapter.title}
                  </Title>
                  {chapter.subtitle ? (
                    <Paragraph className={styles.journeyChapterSubtitle}>{chapter.subtitle}</Paragraph>
                  ) : null}
                </div>

                {chapter.beats.map((beat) => {
                  const reverse = beatIndex % 2 === 1;
                  beatIndex += 1;
                  return (
                    <JourneyBeatRow
                      key={`${chapter.id}-${beat.title}`}
                      title={beat.title}
                      description={beat.description}
                      imageSrc={beat.imageSrc}
                      alt={beat.alt}
                      reverse={reverse}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      {homeCopyEn.imageBreaks.map((b) => (
        <section
          key={b.title}
          className={styles.imageBreak}
          style={{ ['--bgImage' as unknown as string]: `url(${resolvePublicAssetUrl(b.imageSrc)})` }}
        >
          <div className={styles.imageBreakInner}>
            <div className={styles.imageBreakPanel}>
              <Text className={styles.eyebrow}>{b.eyebrow}</Text>
              <Title level={2} className={styles.imageBreakTitle}>
                {b.title}
              </Title>
              <Paragraph className={styles.imageBreakText}>{b.description}</Paragraph>
            </div>
          </div>
        </section>
      ))}

      <section className={styles.section}>
        <div className={styles.container}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Title level={2} className={styles.sectionTitle}>
              {homeCopyEn.sections.workflows.title}
            </Title>
            <Paragraph className={styles.sectionSubtitle}>
              {homeCopyEn.sections.workflows.subtitle}
            </Paragraph>
          </Space>

          <Row gutter={[18, 18]} className={styles.grid}>
            {homeCopyEn.workflows.map((w) => (
              <Col xs={24} md={12} key={w.title}>
                <Card className={styles.glassCard} bordered={false}>
                  <Title level={4} className={styles.cardTitle}>
                    {w.title}
                  </Title>
                  <Text className={styles.cardText}>{w.description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.finalCta}>
            <div className={styles.finalCtaPanel}>
              <Title level={2} className={styles.finalCtaTitle}>
                {homeCopyEn.finalCta.title}
              </Title>
              <Paragraph className={styles.finalCtaSubtitle}>{homeCopyEn.finalCta.subtitle}</Paragraph>
              <div className={styles.finalCtaActions}>
                <Link to="/signin">
                  <Button
                    type="primary"
                    size="large"
                    className={styles.primaryButton}
                    style={{ background: '#478ac9', borderColor: '#478ac9' }}
                  >
                    {homeCopyEn.finalCta.primaryCta}
                  </Button>
                </Link>
                <Link to="/app">
                  <Button size="large" className={styles.secondaryButton}>
                    {homeCopyEn.finalCta.secondaryCta}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
